import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiDollarSign, FiTrendingUp, FiAlertTriangle, FiDownload, FiRefreshCw, FiChevronRight, FiFolder, FiLayers, FiGlobe, FiArrowLeft, FiZap } from 'react-icons/fi';
import FabricFinOpsTab from '../Monitoring/FabricFinOpsTab';
import DashboardLayout from '../Dashboard/DashboardLayout';
import FeatureGate from '../common/FeatureGate';
import { CostBreakdown } from '../canvas_drag_N_DROP/CostBreakdown';
import { CostChart } from '../canvas_drag_N_DROP/CostChart';
import { CostOptimizationRecommendations } from '../canvas_drag_N_DROP/CostOptimizationRecommendations';
import {
  getGlobalCosts,
  getProjectCosts,
  getArchitectureCosts,
  getOptimizationRecommendations,
  GlobalCostsResponse,
  ProjectCostsResponse,
  ProjectCost,
  ArchitectureCost
} from '../../api/cost';

type ViewLevel = 'global' | 'project' | 'architecture';

interface ViewState {
  level: ViewLevel;
  projectId?: string;
  projectName?: string;
  architectureId?: string;
  architectureName?: string;
}

const FinOpsPage: React.FC = () => {
  const { t } = useTranslation('finops');
  const { t: tCommon } = useTranslation('common');
  const [activeFinOpsTab, setActiveFinOpsTab] = useState<'cloud' | 'fabric'>('cloud');
  const [loading, setLoading] = useState(false);
  const [viewState, setViewState] = useState<ViewState>({ level: 'global' });

  // Données
  const [globalData, setGlobalData] = useState<GlobalCostsResponse | null>(null);
  const [projectData, setProjectData] = useState<ProjectCostsResponse | null>(null);
  const [architectureData, setArchitectureData] = useState<any>(null);
  const [optimizationData, setOptimizationData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Charger les données selon le niveau
  useEffect(() => {
    loadData();
  }, [viewState.level, viewState.projectId, viewState.architectureId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (viewState.level === 'global') {
        const data = await getGlobalCosts();
        console.log('Global costs data:', data);
        setGlobalData(data);
        setProjectData(null);
        setArchitectureData(null);
      } else if (viewState.level === 'project' && viewState.projectId) {
        const data = await getProjectCosts(viewState.projectId);
        setProjectData(data);
        setArchitectureData(null);
      } else if (viewState.level === 'architecture' && viewState.architectureId) {
        const data = await getArchitectureCosts(viewState.architectureId);
        setArchitectureData(data);

        // Obtenir les recommandations pour l'architecture
        if (data.estimation?.breakdown) {
          const resources = data.estimation.breakdown.map((b: any) => ({
            id: b.resourceId,
            type: b.resourceType,
            params: {}
          }));
          const opt = await getOptimizationRecommendations(resources, {
            includeReservedInstances: true,
            includeSpotInstances: true
          });
          setOptimizationData(opt);
        }
      }
    } catch (err: any) {
      console.error('Erreur lors du chargement des données:', err);
      setError(err?.response?.data?.error || err?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  // Navigation
  const navigateToProject = (project: ProjectCost) => {
    setViewState({
      level: 'project',
      projectId: project.id,
      projectName: project.name
    });
  };

  const navigateToArchitecture = (arch: ArchitectureCost, projectName?: string) => {
    setViewState({
      level: 'architecture',
      projectId: arch.projectId,
      projectName: projectName || arch.projectName,
      architectureId: arch.id,
      architectureName: arch.name
    });
  };

  const navigateBack = () => {
    if (viewState.level === 'architecture') {
      setViewState({
        level: 'project',
        projectId: viewState.projectId,
        projectName: viewState.projectName
      });
    } else if (viewState.level === 'project') {
      setViewState({ level: 'global' });
    }
  };

  // Calculer le coût total actuel (Cloud uniquement — exclut Fabric)
  const getCurrentCost = () => {
    if (viewState.level === 'global' && globalData) {
      return globalData.projectCosts
        .filter(p => p.provider?.toLowerCase() !== 'fabric')
        .reduce((s, p) => s + p.monthly, 0);
    } else if (viewState.level === 'project' && projectData) {
      return projectData.totalMonthly;
    } else if (viewState.level === 'architecture' && architectureData?.estimation) {
      return architectureData.estimation.totalMonthly;
    }
    return 0;
  };

  const getCurrency = () => {
    if (globalData) return globalData.currency;
    if (projectData) return projectData.currency;
    if (architectureData?.estimation) return architectureData.estimation.currency;
    return 'USD';
  };

  const currentCost = getCurrentCost();
  const currency = getCurrency();
  const budget = 5000;
  const budgetUsage = budget > 0 ? (currentCost / budget) * 100 : 0;
  const forecast = currentCost * 1.07;
  const totalSavingsPotential = optimizationData?.totalSavings || 0;

  // Titre selon le niveau
  const getTitle = () => {
    if (viewState.level === 'global') return t('views.global');
    if (viewState.level === 'project') return viewState.projectName || t('breadcrumb.project');
    if (viewState.level === 'architecture') return viewState.architectureName || t('breadcrumb.architecture');
    return 'FinOps';
  };

  // Breadcrumb
  const renderBreadcrumb = () => {
    const items = [{ label: t('breadcrumb.global'), onClick: () => setViewState({ level: 'global' }) }];

    if (viewState.level === 'project' || viewState.level === 'architecture') {
      items.push({
        label: viewState.projectName || t('breadcrumb.project'),
        onClick: () => setViewState({
          level: 'project',
          projectId: viewState.projectId,
          projectName: viewState.projectName
        })
      });
    }

    if (viewState.level === 'architecture') {
      items.push({
        label: viewState.architectureName || t('breadcrumb.architecture'),
        onClick: () => {}
      });
    }

    return (
      <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
        {items.map((item, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <FiChevronRight size={14} />}
            <button
              onClick={item.onClick}
              className={`hover:text-blue-600 ${idx === items.length - 1 ? 'text-gray-900 font-medium' : ''}`}
            >
              {item.label}
            </button>
          </React.Fragment>
        ))}
      </div>
    );
  };

  // Vue globale - Liste des projets
  const renderGlobalView = () => {
    if (!globalData) return null;

    const cloudProjects = globalData.projectCosts.filter(
      p => p.provider?.toLowerCase() !== 'fabric'
    );
    const cloudTotal = cloudProjects.reduce((s, p) => s + p.monthly, 0);
    const cloudArchCount = cloudProjects.reduce((s, p) => s + p.architecturesCount, 0);

    return (
      <div className="space-y-6">
        {/* Résumé global */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <FiGlobe className="text-blue-600" size={24} />
            <h2 className="text-xl font-bold text-gray-900">{t('views.globalSummary')}</h2>
          </div>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <div className="text-sm text-gray-600">{t('kpi.projects')}</div>
              <div className="text-2xl font-bold text-gray-900">{cloudProjects.length}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">{t('kpi.architectures')}</div>
              <div className="text-2xl font-bold text-gray-900">{cloudArchCount}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">{t('kpi.totalCost')}</div>
              <div className="text-2xl font-bold text-blue-600">{cloudTotal.toFixed(2)} {currency}/{t('costBreakdown.perMonth')}</div>
            </div>
          </div>
        </div>

        {/* Liste des projets */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900">{t('views.costsByProject')}</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {(() => {
              return cloudProjects.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <FiFolder size={48} className="mx-auto mb-4 text-gray-300" />
                <p>{t('views.noProjectsFound')}</p>
                <button
                  onClick={() => window.location.href = '/projects'}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {t('views.createProject')}
                </button>
              </div>
              ) : (
              cloudProjects.map((project) => (
                <div
                  key={project.id}
                  className="p-4 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                  onClick={() => navigateToProject(project)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FiFolder className="text-blue-600" size={20} />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{project.name}</div>
                      <div className="text-sm text-gray-500">
                        {project.architecturesCount} {project.architecturesCount > 1 ? t('projectCard.architectures') : t('projectCard.architecture')} • {project.provider}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="font-bold text-gray-900">{project.monthly.toFixed(2)} {currency}</div>
                      <div className="text-sm text-gray-500">{t('costBreakdown.perMonth')}</div>
                    </div>
                    <FiChevronRight className="text-gray-400" size={20} />
                  </div>
                </div>
              ))
              );
            })()}
          </div>
        </div>

      </div>
    );
  };

  // Vue projet - Liste des architectures
  const renderProjectView = () => {
    if (!projectData) return null;

    return (
      <div className="space-y-6">
        {/* Résumé du projet */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <FiFolder className="text-purple-600" size={24} />
            <h2 className="text-xl font-bold text-gray-900">{projectData.projectName}</h2>
            <span className="px-2 py-1 bg-gray-100 rounded text-sm text-gray-600">{projectData.provider}</span>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-sm text-gray-600">{t('kpi.architectures')}</div>
              <div className="text-2xl font-bold text-gray-900">{projectData.architecturesCount}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">{t('kpi.totalCost')}</div>
              <div className="text-2xl font-bold text-purple-600">{projectData.totalMonthly.toFixed(2)} {currency}/{t('costBreakdown.perMonth')}</div>
            </div>
          </div>
        </div>

        {/* Liste des architectures */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900">{t('views.architectures')}</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {projectData.architectureCosts.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <FiLayers size={48} className="mx-auto mb-4 text-gray-300" />
                <p>{t('views.noArchitecturesFound')}</p>
              </div>
            ) : (
              projectData.architectureCosts.map((arch) => (
                <div
                  key={arch.id}
                  className="p-4 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                  onClick={() => navigateToArchitecture(arch, projectData.projectName)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <FiLayers className="text-purple-600" size={20} />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{arch.name}</div>
                      <div className="text-sm text-gray-500">
                        {arch.resourceCount} {arch.resourceCount > 1 ? t('projectCard.resources') : t('projectCard.resource')}
                        {arch.error && <span className="text-red-500 ml-2">• {t('projectCard.error')}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="font-bold text-gray-900">{arch.monthly.toFixed(2)} {currency}</div>
                      <div className="text-sm text-gray-500">{t('costBreakdown.perMonth')}</div>
                    </div>
                    <FiChevronRight className="text-gray-400" size={20} />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  // Vue architecture - Détails et breakdown
  const renderArchitectureView = () => {
    if (!architectureData?.estimation) return null;
    const estimation = architectureData.estimation;

    return (
      <div className="space-y-6">
        {/* Graphique */}
        {estimation.breakdown && estimation.breakdown.length > 0 && (
          <CostChart
            breakdown={estimation.breakdown}
            currency={currency}
            totalMonthly={estimation.totalMonthly}
          />
        )}

        {/* Breakdown des coûts */}
        {estimation.breakdown && estimation.breakdown.length > 0 && (
          <CostBreakdown
            breakdown={estimation.breakdown}
            currency={currency}
            totalMonthly={estimation.totalMonthly}
            totalHourly={estimation.totalHourly}
          />
        )}

        {/* Recommandations d'optimisation */}
        {optimizationData && optimizationData.recommendations && optimizationData.recommendations.length > 0 && (
          <CostOptimizationRecommendations
            optimizationData={optimizationData}
            currency={currency}
            loading={false}
          />
        )}

        {/* Ressources non supportées */}
        {estimation.unsupported && estimation.unsupported.length > 0 && (
          <div className="bg-yellow-50 rounded-xl border border-yellow-200 p-6">
            <h3 className="text-lg font-bold text-yellow-800 mb-4">
              {t('views.unsupportedResources')} ({estimation.unsupported.length})
            </h3>
            <div className="space-y-2">
              {estimation.unsupported.map((u: any, idx: number) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className="text-yellow-700">{u.resourceType}</span>
                  <span className="text-yellow-600">{u.error}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <DashboardLayout>
      <FeatureGate
        feature="finops_advanced"
        minLevel="standard"
        mode="page"
        lockedMessage="Tableau de bord FinOps"
      >
      <div className="p-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <div className="flex items-center gap-3">
              {activeFinOpsTab === 'cloud' && viewState.level !== 'global' && (
                <button
                  onClick={navigateBack}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <FiArrowLeft size={20} />
                </button>
              )}
              <h1 className="text-3xl font-bold text-gray-900">{t('title')}</h1>
            </div>
            {activeFinOpsTab === 'cloud' && renderBreadcrumb()}
          </div>
          <div className="flex gap-3">
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
            >
              <FiRefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              {tCommon('buttons.refresh')}
            </button>
            <button
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              disabled={!architectureData}
            >
              <FiDownload size={18} />
              {t('export.pdf')}
            </button>
          </div>
        </div>

        {/* Tabs Cloud / Fabric */}
        <div className="flex gap-1 mb-8 bg-gray-100 p-1 rounded-xl w-fit">
          {[
            { key: 'cloud',  label: '☁️ Cloud (Azure / AWS / GCP)' },
            { key: 'fabric', label: '⚡ Microsoft Fabric' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveFinOpsTab(tab.key as 'cloud' | 'fabric')}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                activeFinOpsTab === tab.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Onglet Fabric */}
        {activeFinOpsTab === 'fabric' && <FabricFinOpsTab />}

        {/* Onglet Cloud */}
        {activeFinOpsTab === 'cloud' && <>

        {/* Afficher l'erreur si présente */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
            <div className="font-medium">{t('messages.loadError')}</div>
            <div className="text-sm">{error}</div>
            <button
              onClick={loadData}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
            >
              {tCommon('buttons.retry')}
            </button>
          </div>
        )}

        {loading && !globalData && !projectData && !architectureData ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <FiRefreshCw size={48} className="animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">{t('messages.calculating')}</p>
            </div>
          </div>
        ) : (
          <>
            {/* KPIs Cards - Enhanced with animations and better visuals */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {/* Current Cost - Enhanced */}
              <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-700 text-sm font-semibold">{t('costChart.totalCost')} {getTitle()}</span>
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <FiDollarSign className="text-blue-600" size={24} />
                  </div>
                </div>
                <div className="text-4xl font-bold text-blue-600 mb-2">
                  {currentCost.toFixed(0)} {currency}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600 font-medium">{t('costBreakdown.perMonth')}</span>
                  <div className="flex items-center gap-1 text-green-600 text-xs font-semibold">
                    <span>↓ 3.2%</span>
                  </div>
                </div>
                <div className="mt-3 h-1 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full animate-pulse" style={{ width: '65%' }} />
                </div>
              </div>

              {/* Budget Usage - Enhanced */}
              <div className="bg-gradient-to-br from-white to-purple-50 rounded-xl shadow-lg border border-purple-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-700 text-sm font-semibold">{t('kpi.budgetUsage')}</span>
                  <div className={`p-2 rounded-lg ${budgetUsage > 80 ? 'bg-red-100' : 'bg-purple-100'}`}>
                    <FiTrendingUp className={budgetUsage > 80 ? 'text-red-600' : 'text-purple-600'} size={24} />
                  </div>
                </div>
                <div className="text-4xl font-bold text-purple-600 mb-2">
                  {budgetUsage.toFixed(1)}%
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-gray-600 font-medium">{currentCost.toFixed(0)} / {budget} {currency}</span>
                  <div className={`flex items-center gap-1 text-xs font-semibold ${budgetUsage > 80 ? 'text-red-600' : 'text-green-600'}`}>
                    <span>{budgetUsage > 80 ? '↑' : '↓'} {Math.abs(budgetUsage - 70).toFixed(1)}%</span>
                  </div>
                </div>
                <div className="relative w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      budgetUsage > 95 ? 'bg-gradient-to-r from-red-500 to-red-600' :
                      budgetUsage > 80 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                      'bg-gradient-to-r from-green-500 to-green-600'
                    }`}
                    style={{ width: `${Math.min(budgetUsage, 100)}%` }}
                  />
                </div>
              </div>

              {/* Forecast - Enhanced */}
              <div className="bg-gradient-to-br from-white to-orange-50 rounded-xl shadow-lg border border-orange-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-700 text-sm font-semibold">{t('kpi.forecast')}</span>
                  <div className="bg-orange-100 p-2 rounded-lg">
                    <FiTrendingUp className="text-orange-600" size={24} />
                  </div>
                </div>
                <div className="text-4xl font-bold text-orange-600 mb-2">
                  {forecast.toFixed(0)} {currency}
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-xs">
                    <span className={`font-semibold ${forecast > budget ? 'text-red-600' : 'text-green-600'}`}>
                      {forecast > budget ? '↑' : '↓'} {Math.abs(forecast - budget).toFixed(0)} {currency}
                    </span>
                    <span className="text-gray-500">{t('kpi.vsbudget')}</span>
                  </div>
                  <div className="text-xs text-orange-600 font-semibold">
                    +7%
                  </div>
                </div>
                <div className="mt-3 flex gap-1">
                  {[40, 55, 48, 62, 58, 70, 75].map((height, i) => (
                    <div key={i} className="flex-1 bg-orange-200 rounded-t" style={{ height: `${height}%`, minHeight: '8px' }} />
                  ))}
                </div>
              </div>

              {/* Savings Potential - Enhanced */}
              <div className="bg-gradient-to-br from-green-500 via-green-600 to-emerald-600 rounded-xl shadow-lg p-6 text-white hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-10 rounded-full -ml-12 -mb-12" />
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white text-sm font-semibold">{t('kpi.savingsPotential')}</span>
                    <div className="bg-white bg-opacity-20 p-2 rounded-lg backdrop-blur-sm">
                      <FiAlertTriangle className="text-white" size={24} />
                    </div>
                  </div>
                  <div className="text-4xl font-bold mb-2">
                    {totalSavingsPotential.toFixed(0)} {currency}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-green-100 font-medium">
                      {optimizationData?.recommendations?.length || 0} {t('kpi.recommendations')}
                    </div>
                    <div className="text-xs text-white font-semibold bg-white bg-opacity-20 px-2 py-1 rounded-full">
                      -{currentCost > 0 ? ((totalSavingsPotential / currentCost) * 100).toFixed(0) : 0}%
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <div className="flex-1 bg-white bg-opacity-30 h-1 rounded-full">
                      <div className="bg-white h-full rounded-full" style={{ width: '75%' }} />
                    </div>
                    <span className="text-xs text-white font-semibold">75%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contenu selon le niveau */}
            {viewState.level === 'global' && renderGlobalView()}
            {viewState.level === 'project' && renderProjectView()}
            {viewState.level === 'architecture' && renderArchitectureView()}

            {/* Empty State */}
            {viewState.level === 'global' && globalData && globalData.projectsCount === 0 && (
              <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
                <FiDollarSign size={64} className="text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('views.noDataAvailable')}</h3>
                <p className="text-gray-600 mb-4">
                  {t('views.createProjectCta')}
                </p>
                <button
                  onClick={() => window.location.href = '/projects'}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  {t('views.createProject')}
                </button>
              </div>
            )}
          </>
        )}
        </>} {/* fin onglet Cloud */}
      </div>
      </FeatureGate>
    </DashboardLayout>
  );
};

export default FinOpsPage;
